// src/pages/LandingPage.jsx
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { trackEvent } from '../components/AdMonetization';
import { productsData, PRODUCT_IMAGE_BY_NAME, CUSTOM_SET_SUBSCRIPTION } from '../utils/constants';
import { calculateSetTotals, formatCurrency, getProductCommerceConfig, submitShopifyCheckout } from '../utils/helpers';
import { styles } from '../utils/styles';
import { MobileShop } from './MobileShop';
import { trackInitiateCheckout } from '../utils/pixels';

export function LandingPage({ saveSetToProfile, onAddPoints, savedSets }) {
const [selection, setSelection] = useState([]);
const [focusedItem, setFocusedItem] = useState(null);
// The shop grid is the default view; the custom 6-bottle builder opens from its card
const [building, setBuilding] = useState(false);
const MOBILE_BREAKPOINT = 768;
const [isMobile, setIsMobile] = useState(() => window.innerWidth <= MOBILE_BREAKPOINT);

useEffect(() => {
let debounceTimer;
const handleResize = () => {
clearTimeout(debounceTimer);
debounceTimer = setTimeout(() => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT), 150);
};
window.addEventListener('resize', handleResize);
return () => { clearTimeout(debounceTimer); window.removeEventListener('resize', handleResize); };
}, []);

const handleSelect = (item) => {
setFocusedItem(item);
setSelection(prev => {
if (prev.length >= 6) return prev;
return [...prev, item];
});
};

const handleRemoveFromCart = (name) => {
setSelection(prev => {
const lastIdx = prev.map(i => i.name).lastIndexOf(name);
if (lastIdx === -1) return prev;
return prev.filter((_, i) => i !== lastIdx);
});
};

const selectedItems = selection;
const isSetComplete = selectedItems.length === 6;
const setTotals = calculateSetTotals(selectedItems);
const subscriptionSavings = Math.max(0, setTotals.oneTime - setTotals.subscription);

const handleOneTimeCheckout = () => {
if (!isSetComplete) return;
trackEvent("checkout_started", {
placement: "landing_page",
purchaseType: "one_time",
itemCount: selectedItems.length,
checkoutValue: setTotals.oneTime
});
trackInitiateCheckout(selectedItems.map((item) => ({ id: getProductCommerceConfig(item.name).merchandiseId, name: item.name, price: Number((setTotals.oneTime / selectedItems.length).toFixed(2)), quantity: 1 })));
submitShopifyCheckout(selectedItems, "one-time");
};

const handleSubscriptionCheckout = () => {
if (!isSetComplete) return;
trackEvent("checkout_started", {
placement: "landing_page",
purchaseType: "subscription",
itemCount: selectedItems.length,
checkoutValue: setTotals.subscription
});
trackInitiateCheckout(selectedItems.map((item) => ({ id: getProductCommerceConfig(item.name).merchandiseId, name: item.name, price: Number((setTotals.subscription / selectedItems.length).toFixed(2)), quantity: 1 })));
submitShopifyCheckout(selectedItems, "subscription");
};

const renderRow = (label, category) => (
<div style={styles.rowSection}>
<h3 style={styles.rowLabel}>{label}</h3>
<div style={styles.scrollRow}>
{productsData[category].map(item => {
const isSelected = selection.some(i => i.name === item.name);
const { pricing } = getProductCommerceConfig(item.name);
const resolvedImage = item.imageUrl || item.images?.[0] || PRODUCT_IMAGE_BY_NAME[item.name] || '/logo192.png';
return (
<div key={item.name} onClick={() => handleSelect(item)} style={{ ...styles.card, border: isSelected ? "2px solid #222" : "1px solid #eee" }}>

<img
src={resolvedImage}
alt={item.name}
onError={(e) => {
e.currentTarget.onerror = null;
e.currentTarget.src = PRODUCT_IMAGE_BY_NAME[item.name] || '/logo192.png';
}}
style={{ width: '100%', height: '120px', objectFit: 'contain', borderRadius: '10px', backgroundColor: '#f0f0f0', marginBottom: '8px' }}
/>

<div style={styles.itemName}>{item.name}</div>
<div style={{ fontSize: '11px', color: '#555', marginTop: '8px', lineHeight: '1.5' }}>
<div>One-time {formatCurrency(pricing.oneTime)} <span style={{ color: '#777' }}>· 1 box per month</span></div>
<div>Subscribe in a 6-bottle set <span style={{ color: '#777' }}>· {formatCurrency(CUSTOM_SET_SUBSCRIPTION.setPrice)} / month</span></div>
</div>
</div>
);
})}
</div>
</div>
);

const helmet = (
<Helmet>
<title>The Majorities | Premium Hair Care &amp; Solutions</title>
<meta name="description" content="The Majorities makes premium multicultural hair care and skincare. Build your custom 6-product set from our shampoos, conditioners, oils, face scrubs, toners and creams." />
<link rel="canonical" href="https://themajorities.com/" />
</Helmet>
);

if (!building) {
return (
<div>
{helmet}
<MobileShop setCount={selectedItems.length} onBuildSet={() => { setBuilding(true); window.scrollTo(0, 0); }} />
</div>
);
}

return (
<div>
<button type="button" className="ms-back" onClick={() => { setBuilding(false); window.scrollTo(0, 0); }}>← Back to shop</button>
<Helmet>
<title>The Majorities | Premium Hair Care &amp; Solutions</title>
<meta name="description" content="The Majorities makes premium multicultural hair care and skincare. Build your custom 6-product set from our shampoos, conditioners, oils, face scrubs, toners and creams." />
<link rel="canonical" href="https://themajorities.com/" />
</Helmet>
<h1 style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0 }}>The Majorities — Premium Multicultural Hair Care &amp; Skincare</h1>
<div style={{ ...styles.layout, flexDirection: isMobile ? 'column' : 'row', padding: isMobile ? '20px 16px' : '20px 60px', overflowX: isMobile ? 'hidden' : 'visible', boxSizing: 'border-box' }}>
<div style={{ ...styles.left, width: isMobile ? '100%' : '70%', paddingRight: isMobile ? 0 : '40px', minWidth: 0, overflowX: 'hidden' }}>
{renderRow("Pick Shampoos", "shampoos")}
{renderRow("Pick Conditioners", "conditioners")}
{renderRow("Pick Oils", "oils")}
{renderRow("Pick Face Scrubs", "faceScrubs")}
{renderRow("Pick Toners", "toners")}
{renderRow("Pick Creams", "faceCreams")}
</div>
<aside style={{ ...styles.right, width: isMobile ? '100%' : '30%', position: isMobile ? 'static' : 'sticky', top: isMobile ? 'auto' : '20px', boxSizing: 'border-box', height: 'auto', maxHeight: 'none' }}>

<div style={{ minHeight: '100px', marginBottom: '15px' }}>
{focusedItem ? (
<div>
<h3>{focusedItem.name}</h3>
<div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
<span style={{ fontSize: '12px', fontWeight: '600', color: '#333' }}>
One-time {formatCurrency(getProductCommerceConfig(focusedItem.name).pricing.oneTime)} · 1 box per month
</span>
<span style={{ fontSize: '12px', fontWeight: '600', color: '#2d6a4f' }}>
Subscribe in a 6-bottle set · {formatCurrency(CUSTOM_SET_SUBSCRIPTION.setPrice)} / month
</span>
</div>

<div style={{ fontSize: '13px', color: '#666', lineHeight: '1.6', marginBottom: '20px' }}>
{focusedItem.desc}
</div>

{(focusedItem.images || focusedItem.imageUrl) && (
<div style={{ textAlign: 'center', marginBottom: '20px' }}>
{focusedItem.images ? (
<div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
{focusedItem.images.map((src, idx) => (
<img
key={idx}
src={src}
alt={focusedItem.name + ' view ' + (idx + 1)}
style={{ maxWidth: '30%', maxHeight: '250px', objectFit: 'contain', borderRadius: '12px', border: '1px solid #ddd', padding: '8px', backgroundColor: '#fff', flex: '1 1 28%' }}
/>
))}
</div>
) : (
<img
src={focusedItem.imageUrl}
alt={focusedItem.name}
style={{ maxWidth: '100%', maxHeight: '350px', objectFit: 'contain', borderRadius: '12px', border: '1px solid #ddd', padding: '10px', backgroundColor: '#fff' }}
/>
)}
</div>
)}
</div>
) : <p style={{color: '#888'}}>Select a product</p>}
</div>

<div style={styles.summaryContainer}>
<h4 style={{ fontSize: '14px', borderBottom: '1px solid #eee', paddingBottom: '10px', marginTop: 0 }}>Your Custom Set ({selectedItems.length}/6)</h4>
<div style={{ margin: '10px 0' }}>
{(() => {
const counts = {};
selectedItems.forEach(item => { counts[item.name] = (counts[item.name] || 0) + 1; });
return Object.entries(counts).map(([name, count]) => (
<div key={name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '4px 0' }}>
<p style={{ fontSize: '11px', margin: 0 }}>
{name}{count > 1 ? ` x${count}` : ''} · {formatCurrency(getProductCommerceConfig(name).pricing.oneTime)}
</p>
<button onClick={() => handleRemoveFromCart(name)} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '16px', color: '#aaa', lineHeight: 1, padding: '0 4px' }} title="Remove one">×</button>
</div>
));
})()}
</div>
{isSetComplete ? (
<div style={{ borderTop: '2px solid #222', paddingTop: '15px' }}>
<div style={{ fontSize: '12px', color: '#666', marginBottom: '12px', lineHeight: '1.6' }}>
<div>One-time total: <strong>{formatCurrency(setTotals.oneTime)}</strong> · 1 box per month</div>
<div>Subscription total: <strong>{formatCurrency(setTotals.subscription)} / month</strong> · 1 box per month</div>
<div>You save <strong>{formatCurrency(subscriptionSavings)}</strong> on each monthly set.</div>
</div>
<p style={{ margin: '0 0 14px', fontSize: '12px', color: '#2d6a4f', fontWeight: '700' }}>Subscribe &amp; save — pause or cancel anytime.</p>
<div style={{ backgroundColor: '#f4f9f4', border: '1px solid #c2e1c2', padding: '12px', borderRadius: '8px', marginBottom: '14px', textAlign: 'left' }}>
<span style={{ fontSize: '13px', color: '#1e4620', fontWeight: '700', display: 'block' }}>
Fast US Fulfillment via ShipBob
</span>
<span style={{ fontSize: '11px', color: '#2e6f32', display: 'block', marginTop: '3px' }}>
Estimated Delivery: <strong>{
new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
} - {
new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}</strong> (+ Shipping & Handling)
</span>
</div>
<button style={styles.checkoutBtn} onClick={handleOneTimeCheckout}>1 time Checkout ({formatCurrency(setTotals.oneTime)})</button>
<button style={{ ...styles.checkoutBtn, background: '#222', color: '#fff' }} onClick={handleSubscriptionCheckout}>Monthly Subscription ({formatCurrency(setTotals.subscription)} / Month)</button>
<div style={{ padding: '12px', marginTop: '4px', borderRadius: '8px', background: '#fff', border: '1px solid #dce9dc', fontSize: '11px', lineHeight: '1.5', color: '#444' }}>
<strong style={{ display: 'block', color: '#222' }}>🔒 Secure checkout</strong>
Your payment details are securely processed.
</div>
<div style={{ padding: '12px', marginTop: '10px', borderRadius: '8px', background: '#fff', border: '1px solid #eee', fontSize: '11px', lineHeight: '1.5', color: '#444' }}>
<strong style={{ display: 'block', color: '#222' }}>30-day returns</strong>
Return unused items in original packaging within 30 days of delivery. <Link to="/returns" style={{ color: '#222', fontWeight: '700' }}>Read the full policy</Link>
</div>
<div style={{ padding: '12px', marginTop: '10px', borderRadius: '8px', background: '#fff', border: '1px solid #eee', fontSize: '11px', lineHeight: '1.5', color: '#444' }}>
<strong style={{ display: 'block', color: '#222' }}>★★★★★ Loved by our customers</strong>
"My hair feels softer without adding extra steps." — Jordan R.
</div>
</div>
) : <p style={{ fontSize: '12px', color: '#888' }}>Select 6 products to checkout</p>}
</div>
</aside>
</div>
</div>
);
}
